import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { api } from '@/api/client'
import type { Notification } from '@/mocks/data/types'
import { createRequestSequencer } from './utils/requestSequencer'

interface NotificationListResponse {
  data: Notification[]
  meta: { total: number; limit: number; offset: number; has_more: boolean }
  unread_count: number
}

interface NotificationsState {
  notifications: Notification[]
  /** The server's unread count over every owner-facing row, not just the loaded page. */
  unreadCount: number
  isLoading: boolean
  /**
   * True once any fetch has settled (success or failure). The bell reads this
   * so it never renders a confident "You're all caught up!" empty state over
   * rows the still-pending first fetch is about to deliver (#2209).
   */
  hasLoaded: boolean
  error: string | null
}

interface NotificationsActions {
  fetchNotifications: () => Promise<void>
  markAsRead: (notificationId: string) => void
  markAllAsRead: () => void
  getUnreadCount: () => number
}

type NotificationsStore = NotificationsState & NotificationsActions

const initialState: NotificationsState = {
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  hasLoaded: false,
  error: null,
}

// Sequences overlapping fetchNotifications calls so a stale (out-of-order)
// resolution can never leave a stuck error over freshly loaded rows (issue #1313).
const notificationsFetchSeq = createRequestSequencer()

export const useNotificationsStore = create<NotificationsStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetchNotifications: async () => {
        const token = notificationsFetchSeq.begin()
        set({ isLoading: true, error: null })
        try {
          const response = await api.get<NotificationListResponse>('/notifications')
          // A newer fetchNotifications superseded this one — discard the result.
          if (!notificationsFetchSeq.isCurrent(token)) return
          set({
            notifications: response.data,
            unreadCount: response.unread_count,
            isLoading: false,
            hasLoaded: true,
          })
        } catch {
          // A newer fetchNotifications superseded this one — never surface a
          // stale error over rows another request already loaded successfully.
          if (!notificationsFetchSeq.isCurrent(token)) return
          set({ error: 'Failed to fetch notifications', isLoading: false, hasLoaded: true })
        }
      },

      markAsRead: (notificationId: string) => {
        const now = new Date().toISOString()
        set((state) => {
          const wasUnread = state.notifications.some(
            (notification) => notification.id === notificationId && !notification.read_at
          )
          return {
            notifications: state.notifications.map((notification) =>
              notification.id === notificationId ? { ...notification, read_at: now } : notification
            ),
            unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
          }
        })
        // Sync with backend using the real endpoint
        api.patch(`/notifications/${notificationId}/read`).catch(() => {
          // Silently fail - we already updated the UI optimistically
        })
      },

      markAllAsRead: () => {
        const now = new Date().toISOString()
        set((state) => ({
          notifications: state.notifications.map((notification) => ({
            ...notification,
            read_at: notification.read_at ?? now,
          })),
          unreadCount: 0,
        }))
        // Sync with backend using the real endpoint
        api.patch('/notifications/read-all').catch(() => {
          // Silently fail - we already updated the UI optimistically
        })
      },

      getUnreadCount: () => {
        return get().unreadCount
      },
    }),
    { name: 'NotificationsStore', enabled: import.meta.env.DEV }
  )
)

// Selectors
export const selectNotifications = (state: NotificationsStore) => state.notifications
export const selectUnreadNotifications = (state: NotificationsStore) =>
  state.notifications.filter((notification) => !notification.read_at)
export const selectIsLoading = (state: NotificationsStore) => state.isLoading
